import { LUCIDE_ICONS, LucideIconProviderInterface } from 'lucide-angular';
import { EnvironmentProviders, Provider, inject, provideAppInitializer } from '@angular/core';

/** Dibujo de un icono, tal como lo entrega lucide. */
type DibujoDeIcono = ReturnType<NonNullable<LucideIconProviderInterface['getIcon']>>;

/**
 * Proveedor de RESPALDO que cubre el catalogo COMPLETO de lucide.
 *
 * <p>El menu vive en la base de datos y cada opcion nombra su icono; el registro, en cambio, vivia
 * en el codigo del shell. Quien creaba una opcion no tenia como saber que ademas debia parchear
 * TypeScript, de modo que la opcion aparecia SIN icono y nadie se enteraba hasta verla. Con este
 * proveedor el nombre que traiga la base se resuelve solo, sin tocar codigo.</p>
 *
 * <p>El catalogo NO se declara aqui: se pide con un `import()` dentro de {@link cargar}, y eso es
 * lo que lo deja en un archivo APARTE en vez de engordar el bundle de arranque. El archivo se baja
 * en paralelo con las primeras llamadas del arranque y, como los iconos casi nunca cambian, su
 * huella se mantiene entre despliegues: el navegador re-baja la aplicacion, no los iconos.</p>
 *
 * <p>Es el ULTIMO de la cadena. Los proveedores declarados antes —los del kit y los del shell— se
 * consultan primero y siguen resolviendo lo suyo, de modo que si este catalogo no llegara a
 * cargar, todo lo que hoy se pinta se seguiria pintando igual.</p>
 */
export class CatalogoLucideCompleto implements LucideIconProviderInterface {
  /** Catalogo ya cargado. Vacio mientras el archivo aparte no haya llegado. */
  private catalogo: Record<string, DibujoDeIcono> = {};

  /**
   * Trae el catalogo completo.
   *
   * <p>Si la descarga falla, se deja el catalogo vacio y se sigue: este proveedor es un respaldo,
   * y quedarse sin el devuelve la pantalla al comportamiento que tenia antes de existir. Tumbar el
   * arranque de la aplicacion por un icono seria mucho peor que un icono en blanco.</p>
   */
  async cargar(): Promise<void> {
    try {
      // Se pide el punto de entrada que SOLO trae iconos, no la raiz del paquete. La raiz ya
      // entra en el arranque —el kit y los registros explicitos la importan—, de modo que pedirla
      // aqui no separaria nada: el catalogo entero acabaria dentro del bundle de arranque.
      const catalogo = await import('lucide-angular/src/icons');
      this.catalogo = catalogo as unknown as Record<string, DibujoDeIcono>;
    } catch {
      this.catalogo = {};
    }
  }

  /**
   * Indica si el catalogo conoce el icono.
   *
   * @param nombre nombre del icono en notacion Pascal, como lo declara el menu
   */
  hasIcon(nombre: string): boolean {
    return Object.prototype.hasOwnProperty.call(this.catalogo, nombre);
  }

  /**
   * Devuelve el dibujo del icono.
   *
   * @param nombre nombre del icono en notacion Pascal
   * @returns el dibujo, o null si el catalogo no lo tiene
   */
  getIcon(nombre: string): DibujoDeIcono {
    return this.hasIcon(nombre) ? this.catalogo[nombre] : null;
  }
}

/**
 * Registra el catalogo completo de lucide como ultimo recurso de la cadena de iconos.
 *
 * <p>Va DESPUES de los registros explicitos, que se consultan primero. Se carga durante el
 * arranque, antes de que la aplicacion pinte, para que no haya un instante con iconos en blanco.</p>
 *
 * @returns los proveedores que hay que declarar en la aplicacion
 */
export function proveerCatalogoLucideCompleto(): (Provider | EnvironmentProviders)[] {
  const catalogo = new CatalogoLucideCompleto();
  return [
    { provide: CatalogoLucideCompleto, useValue: catalogo },
    { provide: LUCIDE_ICONS, useValue: catalogo, multi: true },
    provideAppInitializer(() => inject(CatalogoLucideCompleto).cargar()),
  ];
}
